'use client';

import {LocalizedHtml, useI18n} from '@udemy-v2/i18n';
import {AlertBanner} from '@udemy-v2/react-notice-components';
import {Loader} from '@udemy-v2/react-reveal-components';

import {useEnrolledInCourse} from '@/features/course-landing-page/hooks/use-enrolled-in-course';
import {getCourseLearnUrl} from '@/lib/utils';

import styles from './unavailable-course.module.scss';

interface UnavailableCourseProps {
    /** Course id from the ACCESS_RESTRICTED extension; required to look up enrollment. */
    courseId?: string;
    /** Source URL (e.g. https://udemy.com/course/foo/) used to derive the /learn/ link. */
    courseLandingUrl: string;
    /** topic, category, subcategory IDs threaded through from the extension. Used in phase 2. */
    topicId?: string;
    categoryId?: string;
    subcategoryId?: string;
}

interface UnavailableCourseBannerProps {
    /** True when the viewer is enrolled — surfaces the /learn/ deep link. */
    isEnrolled: boolean;
    courseLandingUrl: string;
}

/**
 * Pure presentational banner. Exported so Storybook can render both
 * enrollment branches without standing up a GraphQL mock.
 */
export function UnavailableCourseBanner({
    isEnrolled,
    courseLandingUrl,
}: UnavailableCourseBannerProps) {
    const {gettext, interpolate} = useI18n();
    const learnUrl = getCourseLearnUrl(courseLandingUrl);

    const body = isEnrolled ? (
        <LocalizedHtml
            html={interpolate(
                gettext(
                    'Since you previously enrolled in this course, you can still access it <a class="course-learn-link">here</a>.',
                ),
                {},
                true,
            )}
            interpolate={{
                'course-learn-link': <a className="ud-link-underline" href={learnUrl} />,
            }}
        />
    ) : (
        <LocalizedHtml
            html={gettext('If you previously enrolled in this course, please log in to access.')}
            interpolate={{}}
        />
    );

    return (
        <AlertBanner
            udStyle="warning"
            showCta={false}
            iconDismissButton={false}
            title={gettext('Sorry, this course is no longer accepting enrollments.')}
            body={body}
            className={styles['alert-banner']}
        />
    );
}

/**
 * Rendered by `LimitedAccessCLP` for the `UNAVAILABLE` restriction. Shows a
 * warning banner explaining the course no longer accepts new enrollments;
 * the subtitle switches based on whether the current user is already
 * enrolled (in which case we surface a deep link to `/learn/`).
 *
 * Phase 2 of CVR-2430 will mount a recommendations carousel below this
 * banner using the topic/category/subcategory IDs threaded through here.
 */
export function UnavailableCourse(props: UnavailableCourseProps) {
    // topicId, categoryId, subcategoryId are accepted on the interface so the
    // page routes can already pass them through; the carousel that consumes
    // them lands in phase 2.
    const {courseId, courseLandingUrl} = props;
    // When the extension didn't include a courseId we have nothing to look
    // up; skip the loader and render the not-enrolled copy directly.
    const enrollment = useEnrolledInCourse(courseId ?? '');
    const isEnrolled = courseId ? enrollment : false;

    if (courseId && isEnrolled === undefined) {
        return (
            <div className={styles['container']}>
                <Loader size="xxlarge" />
            </div>
        );
    }

    return (
        <div className={styles['container']}>
            <UnavailableCourseBanner
                isEnrolled={isEnrolled === true}
                courseLandingUrl={courseLandingUrl}
            />
        </div>
    );
}
